import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  MdHome, MdLocalHospital, MdCalendarToday,
  MdPerson, MdWarning, MdLogout, MdMenu, MdClose, MdMedicalServices,
  MdRedeem, MdSupport, MdMedication,
} from 'react-icons/md';

const NAV = [
  { path: '/',            icon: <MdHome />,             label: 'Home' },
  { path: '/hospitals',   icon: <MdLocalHospital />,    label: 'Hospitals' },
  { path: '/find-doctor', icon: <MdMedicalServices />,  label: 'Find Doctor' },
  { path: '/bookings',    icon: <MdCalendarToday />,    label: 'My Appointments' },
  { path: '/emergency',   icon: <MdWarning />,          label: '🚨 Emergency' },
  { path: '/refer',       icon: <MdRedeem />,           label: 'Refer & Earn' },
  { path: '/prescriptions', icon: <MdMedication />,        label: 'Prescriptions' },
  { path: '/support',     icon: <MdSupport />,          label: 'Help & Support' },
  { path: '/profile',     icon: <MdPerson />,           label: 'Profile' },
];

export default function SideNav() {
  const navigate         = useNavigate();
  const { pathname }     = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen]  = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 10);
  };

  const navItems = (
    <nav className="sidebar-nav">
      {NAV.map(n => (
        <button
          key={n.path}
          className={`nav-item${pathname === n.path ? ' active' : ''}`}
          onClick={(e) => { e.stopPropagation(); go(n.path); }}
        >
          {n.icon} <span>{n.label}</span>
        </button>
      ))}
    </nav>
  );

  const logoArea = (
    <div className="sidebar-logo" onClick={() => go('/')} style={{ cursor: 'pointer' }}>
      <img
        src="/hcen_logo.png"
        alt="HCEN"
        style={{
          width: 54, height: 54, borderRadius: 14,
          objectFit: 'cover', marginBottom: 8,
          boxShadow: '0 4px 16px rgba(211,47,47,0.25)',
          border: '2px solid #ffcdd2',
        }}
      />
      <div className="sidebar-logo-text">HCEN</div>
      <div className="sidebar-logo-sub">Healthcare Emergency Network</div>
    </div>
  );

  const footerArea = (
    <div className="sidebar-footer">
      {user ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            Logged in as <strong style={{ color: 'var(--text)' }}>{user.name || user.phone}</strong>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={logout} style={{ width: '100%', gap: 8 }}>
            <MdLogout /> Sign Out
          </button>
        </div>
      ) : (
        <button className="btn btn-primary btn-sm" onClick={() => go('/login')} style={{ width: '100%' }}>
          Login / Sign Up
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* ── Desktop Sidebar ────────────────────────────────────── */}
      <aside className="sidebar sidebar-desktop">
        {logoArea}
        {navItems}
        {footerArea}
      </aside>

      {/* ── Mobile Top Bar ─────────────────────────────────────── */}
      <header className="mobile-topbar">
        <button className="hamburger-btn" onClick={() => setOpen(true)}>
          <MdMenu />
        </button>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/hcen_logo.png" alt="HCEN" style={{ width:36, height:36, borderRadius:10, objectFit:'cover', border:'2px solid #ffcdd2' }} />
          <div>
            <div style={{ fontSize:17, fontWeight:900, color:'var(--red)', lineHeight:1 }}>HCEN</div>
            <div style={{ fontSize:9, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:600 }}>Healthcare Emergency Network</div>
          </div>
        </div>
        <div style={{ width:44 }} />
      </header>

      {/* ── Overlay — behind drawer, closes on tap ─────────────── */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 8000,
          }}
        />
      )}

      {/* ── Mobile Drawer — always on top via inline zIndex ────── */}
      <aside
        style={{
          position: 'fixed',
          top: 0, left: 0, bottom: 0,
          width: 280,
          background: '#fff',
          zIndex: 9000,                    /* always above overlay */
          display: 'flex',
          flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: '4px 0 32px rgba(0,0,0,0.2)',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}  /* stop overlay closing when tapping drawer */
      >
        {/* Close X */}
        <button
          onClick={() => setOpen(false)}
          style={{
            position:'absolute', top:12, right:12,
            width:36, height:36, borderRadius:'50%',
            border:'none', background:'#ffebee',
            color:'#d32f2f', fontSize:20, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            zIndex:1,
          }}
        >
          <MdClose />
        </button>

        {/* Rainbow stripe */}
        <div style={{ height:4, background:'linear-gradient(90deg,#d32f2f,#ef5350 33%,#66bb6a 66%,#42a5f5)', flexShrink:0 }} />

        {logoArea}
        {navItems}
        {footerArea}
      </aside>
    </>
  );
}
